import {
	Dialog,
	Form,
	FormattedText,
	Tabs,
	Toolbar,
	useConfirmationDialog
} from "client/Components";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useUnmountedCancellation,
	useWarnBeforeUnload
} from "client/Hooks";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { IFormHandler, useFormHandler } from "client/Forms";
import { localize } from "client_legacy/ndaw/culture";
import {
	CommunityKind,
	EditCommunityPageViewModel,
	EditCommunityViewModel,
	getEditModel,
	getNewModel,
	SaveCommunityModel,
	create,
	edit,
	CreateCommunityResult,
	CommunityImageRestrictions,
	CommunityCoverImageViewModel,
	CommunityCoverImageSaveModel,
	getDirectUserMembersOfUserGroups
} from "client/Api/Communities/community";
import { PermissionSetModel } from "client/Api/common";
import { Settings } from "./Settings";
import { Authors } from "./Authors";
import { Members } from "./Members";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	doesNotExceed,
	doesNotExceedLength,
	hasMaxSize,
	isNotEmpty,
	isNotLessThan,
	isNotNullNorUndefined,
	isSupportedFileType
} from "client/Validation/rules";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	getFileMetadata,
	isNil,
	isNilOrWhiteSpace,
	isNotNil
} from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import styles from "./EditCommunityDialog.module.less";
import { UserGridItem } from "client/Api/UserManagement/userManagement";
import { unique, without } from "client_legacy/ndaw/arrayHelpers";
import { Owners } from "./Owners";
import { Base64FileUriUploadWithMetadataModel } from "client/Misc/commonTypes";
import { IObjectInputAttachable } from "client/Forms/attachable";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import { DEFAULT_FOCAL_POINT } from "../../FocalPointDialog/FocalPointEditorSection";
import { FocalPoint, useFocalPointDialog } from "../../FocalPointDialog/FocalPointDialog";
import { getCommunityImageUrl } from "../common";
import { formatList } from "client/Misc/cultureSpecificUtils";
import { CommunityEvent, publish as publishCommunityEvent } from "../events";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";

export type EditCommunityDialogProps = {
	communityId?: number;
	onClose: () => void;
	afterSave: (result: SaveCommunityResult) => void;
};

export const EditCommunityDialog = (props: EditCommunityDialogProps): ReactElement => {
	const {
		onClose: onCloseFromProps,
		communityId,
		afterSave
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [imageRestrictions, setImageRestrictions] = useState<CommunityImageRestrictions>(defaultImageRestrictions);
	const [existingImage, setExistingImage] = useState<CommunityCoverImageViewModel | null>(null);
	const [lastPersistedFormData, setLastPersistedFormData] = useState<FormModel>(initialFormData);
	const [formData, setFormData] = useState<FormModel>(initialFormData);
	const [communityData, setCommunityData] = useState<EditCommunityData>({
		existingMembers: [],
		usersAndUserGroupsData: {
			userGroups: [],
			users: []
		}
	});

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<FormModel> => {
			const result: IValidationConfiguration<FormModel> = staticValidationConfiguration.clone();

			const {
				maxFileSize,
				supportedImageTypes
			} = imageRestrictions;

			result
				.forObject("image")
				.whenType(isNotNil)
				.forObject("file")
				.add(
					hasMaxSize(maxFileSize),
					isSupportedFileType(supportedImageTypes)
				);

			return result;
		},
		[imageRestrictions]
	);
	const formHandler: IFormHandler<FormModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [focalPointDialog, openFocalPointDialog] = useFocalPointDialog();

	const hasChanges = useMemo(
		(): boolean => !deepComparison(lastPersistedFormData, formData),
		[formData, lastPersistedFormData]
	);

	useWarnBeforeUnload(hasChanges && !isBusy);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const onClose: typeof onCloseFromProps = useStableImperativeCallback(onCloseFromProps);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const validationResult: InputValidationResult = await formHandler.validation
				.validate();

			if (validationResult.kind !== InputValidationResultKind.Valid) {
				setIsBusy(false);
				return;
			}

			const saveModel: SaveCommunityModel = convertFormModelToSaveCommunityModel(
				formData,
				communityData.existingMembers
			);

			let id: number | undefined = communityId;
			try {
				if (isNil(id)) {
					const createResult: CreateCommunityResult = await create(
						saveModel,
						globalCancellation
					);

					id = createResult.id;

					const { userId } = userConfiguration()
						.currentUserIdentity;

					publishCommunityEvent(
						CommunityEvent.CommunityCreated,
						{
							id,
							name: saveModel.name,
							isMember: formData.members
								.some(x => x === userId)
						}
					);
				} else {
					await edit(
						saveModel,
						id,
						globalCancellation
					);
				}

				afterSave({
					id,
					name: saveModel.name
				});

				onClose();

			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!formHandler.validation
					.tryProcessServerError(e)
				) {
					alertManager.showError(localize("_ChangesNotSaved"));
					console.error(e);
				}

				setIsBusy(false);
			}
		},
		[
			communityData.existingMembers,
			isBusy,
			formHandler,
			communityId,
			formData,
			alertManager,
			globalCancellation,
			onClose,
			afterSave
		]
	);

	const closeHandler = useCallback(
		async (): Promise<void> => {
			if (hasChanges) {
				const result: TryResult<void> = await openConfirmationDialog({
					title: localize("_AreYouSureYouWantToLeaveThisPage"),
					children: localize("_YouHaveUnsavedChanges"),
					confirmText: localize("_DiscardChanges"),
					cancelText: localize("_Cancel"),
					trackingId: "discard-community-changes-confirmation-dialog"
				});

				if (!result.wasSuccessful) {
					return;
				}
			}

			onClose();
		},
		[
			hasChanges,
			onClose,
			openConfirmationDialog
		]
	);

	const isExisting: boolean = isNotNil(communityId);

	useEffect(
		(): void | (() => void) => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);
					let model: EditCommunityPageViewModel;

					if (isNil(communityId)) {
						model = await getNewModel(signal);
					} else {
						model = await getEditModel(communityId, signal);
					}

					const {
						community,
						usersAndUserGroupsData,
						imageRestrictions
					} = model;

					setImageRestrictions(imageRestrictions);

					setExistingImage(community.image);

					const isAllCompany: boolean = community.kind === CommunityKind.AllCompany;
					const allCompanyMembersIds: readonly number[] = usersAndUserGroupsData.users
						.map(x => x.id);

					const existingMembers: readonly number[] = isAllCompany
						? allCompanyMembersIds
						: community.members;

					setCommunityData({
						usersAndUserGroupsData,
						existingMembers
					});

					const formModel: FormModel = convertEditCommunityViewModelToFormModel(
						{
							...community,
							members: existingMembers
						}
					);

					setLastPersistedFormData(formModel);
					setFormData(formModel);

					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return (): void => cancellation.abort();
		},
		[
			alertManager,
			communityId,
			onClose
		]
	);

	const canOnlyEditPermissionsAndCoverImage: boolean = formData.kind === CommunityKind.AllCompany;

	const afterPermissionSetChanged = useCallback(
		async (
			kind: PermissionKind,
			oldValue: PermissionSetModel,
			newValue: PermissionSetModel
		): Promise<void> => {
			if (canOnlyEditPermissionsAndCoverImage) {
				return;
			}

			const addedGroupsId = without(
				newValue.userGroupIds,
				oldValue.userGroupIds
			);

			const addedUsersIds = without(
				newValue.userIds,
				oldValue.userIds
			);

			if (addedGroupsId.length === 0 && addedUsersIds.length === 0) {
				return;
			}

			const { titleKey, bodyKey } = permissionKindToAddMemberDialogConfigurations[kind];

			const result: TryResult<void> = await openConfirmationDialog({
				trackingId: "add-as-member-confirmation-dialog",
				title: localize(titleKey),
				children: localize(bodyKey),
				confirmText: localize("_AddAsMember"),
				cancelText: localize("_DontAddAsMember")
			});

			if (!result.wasSuccessful) {
				return;
			}

			const newAuthorsId: number[] = [...addedUsersIds];

			try {
				if (addedGroupsId.length > 0) {
					setIsBusy(true);
					const groupUsers: readonly UserGridItem[] = await getDirectUserMembersOfUserGroups(
						addedGroupsId,
						globalCancellation
					);

					newAuthorsId.push(
						...groupUsers
							// Simulate SelectUserAndUserGroupDialog where deleted users are not up for selection
							.filter((x) => isNil(x.deletionDate))
							.map(x => x.id)
					);

					setIsBusy(false);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);
				alertManager.showError(localize("_GeneralErrorMessage"));
				console.error(e);
			}

			formHandler.for("members")
				.reducer
				.setValue(
					unique([
						...formHandler.value
							.members,
						...newAuthorsId
					])
				);

			formHandler.for("members")
				.validation
				.triggerValueChanged();
		},
		[
			alertManager,
			canOnlyEditPermissionsAndCoverImage,
			formHandler,
			globalCancellation,
			openConfirmationDialog
		]
	);

	const afterAuthorsChangedHandler = useCallback(
		async (
			oldValue: PermissionSetModel,
			newValue: PermissionSetModel
		): Promise<void> => {
			await afterPermissionSetChanged(
				"author",
				oldValue,
				newValue
			);
		},
		[afterPermissionSetChanged]
	);

	const afterOwnersChangedHandler = useCallback(
		async (
			oldValue: PermissionSetModel,
			newValue: PermissionSetModel
		): Promise<void> => {
			await afterPermissionSetChanged(
				"owner",
				oldValue,
				newValue
			);
		},
		[afterPermissionSetChanged]
	);

	const imageAttachable: IObjectInputAttachable<CommunityCoverImageSaveModel | null> = formHandler.for("image");

	const setImage = useCallback(
		(image: CommunityCoverImageSaveModel | null): void => {
			imageAttachable
				.reducer
				.setValue(image);

			imageAttachable
				.validation
				.clearServerErrors();

			if (isNil(image)) {
				imageAttachable.validation
					.triggerInputFinished();
			} else {
				// setTimeout is needed: the attachable can still be unset at this point when it went
				// from null to set. In order to trigger the validation, we need to wait for the attachable
				// to contain the new value in order to meet the whenSet condition that will trigger the
				// validation
				setTimeout(
					(): void => {
						imageAttachable.whenSet(
							(x) => {
								x.for("file")
									.validation
									.triggerInputFinished();

								x.for("focalPoint")
									.validation
									.triggerInputFinished();
							}
						);
					},
					1
				);
			}
		},
		[imageAttachable]
	);

	const fileChangeHandler = useCallback(
		async (file: Base64FileUriUploadWithMetadataModel | null): Promise<void> => {
			const { maxFileSize, supportedImageTypes } = imageRestrictions;

			if (!file) {
				setImage(null);
				return;
			}

			const { size, mimeType } = getFileMetadata(file);

			let focalPoint: FocalPoint = DEFAULT_FOCAL_POINT;

			if (size > maxFileSize) {
				alertManager.showError(
					<FormattedText
						text={localize("_MaxSizeIs")}
						thingsToReplace={[
							{
								what: "{0}",
								filler: getDisplayFriendlySize(maxFileSize)
							}
						]}
					/>
				);
			} else if (!supportedImageTypes.includes(mimeType)) {
				alertManager.showError(
					<FormattedText
						text={localize("_SupportedImageFormats")}
						thingsToReplace={[
							{
								what: "{0}",
								filler: formatList([
									localize("_FileExtensionJpg"),
									localize("_FileExtensionPng"),
									localize("_FileExtensionGif")
								])
							}
						]}
					/>
				);
			} else {
				const focalPointResult = await openFocalPointDialog({
					context: "creating-item",
					initialFocalPoint: DEFAULT_FOCAL_POINT,
					initialImageUrl: file.uri,
					options: {
						maxImageSize: maxFileSize,
						supportedImageTypes
					}
				});

				if (focalPointResult.wasSuccessful) {
					const {
						focalPoint: configuredFocalPoint,
						image
					} = focalPointResult.result;

					focalPoint = configuredFocalPoint;

					if (image.isSet) {
						file = image.value;
					}
				}
			}

			setImage({
				file: {
					isSet: true,
					value: file
				},
				focalPoint
			});
		},
		[
			imageRestrictions,
			setImage,
			alertManager,
			openFocalPointDialog
		]
	);

	const editFocalPointHandler = useCallback(
		async (): Promise<void> => {
			const image: CommunityCoverImageSaveModel = ensureNotNil(imageAttachable.value);

			const {
				file,
				focalPoint: initialFocalPoint
			} = image;

			let initialImageUrl: string;

			if (file.isSet) {
				initialImageUrl = file.value
					.uri;
			} else {
				const { blobId } = ensureNotNil(existingImage)
					.file;

				initialImageUrl = getCommunityImageUrl(blobId);
			}

			const { maxFileSize, supportedImageTypes } = imageRestrictions;

			const focalPointResult = await openFocalPointDialog({
				context: "editing-item",
				initialFocalPoint,
				initialImageUrl,
				options: {
					maxImageSize: maxFileSize,
					supportedImageTypes
				}
			});

			if (!focalPointResult.wasSuccessful) {
				return;
			}

			const {
				focalPoint: newFocalPoint,
				image: newImage
			} = focalPointResult.result;

			setImage({
				file: newImage.isSet
					? newImage
					: file,
				focalPoint: newFocalPoint
			});

		},
		[
			existingImage,
			imageAttachable.value,
			imageRestrictions,
			openFocalPointDialog,
			setImage
		]
	);

	const isSaveButtonDisabled: boolean = isNilOrWhiteSpace(formData.name);
	const hasOtherLeftSliderDialogOpen: boolean = isNotNil(focalPointDialog);

	return (
		<>
			<Dialog
				trackingId="edit-community-dialog"
				hasBusyIndicator={isBusy}
				variant="left-slider"
				onClose={closeHandler}
				backdrop={
					hasOtherLeftSliderDialogOpen
						? "invisible"
						: undefined
				}
			>
				<Dialog.Header
					title={localize(
						isExisting
							? "_EditCommunity"
							: "_NewCommunity"
					)}
					onClose={closeHandler}
					hasBorder
				/>
				<Dialog.Body>
					<Form onSubmit={saveHandler} height="fill">
						<Tabs<TabId> height="fill">
							<Tabs.Tab
								id={settingsTabId}
								title={localize("_Settings")}
							>
								<div className={styles.tab}>
									<Settings
										attachable={formHandler}
										areNameAndDescriptionEditable={!canOnlyEditPermissionsAndCoverImage}
										originalImage={existingImage}
										imageRestrictions={imageRestrictions}
										onFileChange={fileChangeHandler}
										onEditFocalPoint={editFocalPointHandler}
									/>
								</div>
							</Tabs.Tab>

							<Tabs.Tab
								id={authorsTabId}
								title={localize("_Authors")}
							>
								<div className={styles.tab}>
									<Authors
										attachable={formHandler.for("authors")}
										usersAndUserGroupsData={communityData.usersAndUserGroupsData}
										onAfterAuthorsChanged={afterAuthorsChangedHandler}
									/>
								</div>
							</Tabs.Tab>

							<Tabs.Tab
								id={ownersTabId}
								title={localize("_Owners")}
							>
								<div className={styles.tab}>
									<Owners
										attachable={formHandler.for("owners")}
										usersAndUserGroupsData={communityData.usersAndUserGroupsData}
										onChanged={afterOwnersChangedHandler}
									/>
								</div>
							</Tabs.Tab>

							<Tabs.Tab
								id={membersTabId}
								title={localize("_Members")}
							>
								<div className={styles.tab}>
									<Members
										attachable={formHandler.for("members")}
										usersAndUserGroupsData={communityData.usersAndUserGroupsData}
										nonDeletableMembersIds={communityData.existingMembers}
										isEditable={!canOnlyEditPermissionsAndCoverImage}
									/>
								</div>
							</Tabs.Tab>
						</Tabs>
					</Form>
				</Dialog.Body>
				<Dialog.Footer hasBorder>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={localize(
								isExisting
									? "_Save"
									: "_Create"
							)}
							onClick={saveHandler}
							trackingId="save"
							isDisabled={isSaveButtonDisabled}
						/>
						<Toolbar.Button
							kind="secondary"
							text={localize("_Cancel")}
							onClick={closeHandler}
							trackingId="cancel"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>

			{focalPointDialog}
			{confirmationDialog}
		</>
	);
};
EditCommunityDialog.displayName="EditCommunityDialog";

export type SaveCommunityResult = {
	id: number;
	name: string;
};

export type AuthorsFormModel = {
	everyoneWhoHasAccess: boolean;
	specificSet: PermissionSetModel;
};

type EditCommunityData = {
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
	existingMembers: readonly number[];
};

export type FormModel = Omit<SaveCommunityModel, "authors" | "memberChanges" | "image">
	& {
		kind: CommunityKind;
		authors: AuthorsFormModel;
		members: readonly number[];
		image: OverrideProperties<
			CommunityCoverImageSaveModel,
			{
				file: OptionalModel<Base64FileUriUploadWithMetadataModel>;
			}
		> | null;
	};

const initialFormData: FormModel = {
	kind: CommunityKind.Custom,
	description: "",
	image: null,
	name: "",
	members: EMPTY_ARRAY,
	owners: {
		userIds: EMPTY_ARRAY,
		userGroupIds: EMPTY_ARRAY
	},
	authors: {
		everyoneWhoHasAccess: true,
		specificSet: {
			userGroupIds: EMPTY_ARRAY,
			userIds: EMPTY_ARRAY
		}
	}
};

const settingsTabId = "settings";
const authorsTabId = "authors";
const ownersTabId = "owners";
const membersTabId = "members";

type TabId =
	| typeof settingsTabId
	| typeof authorsTabId
	| typeof ownersTabId
	| typeof membersTabId;

const staticValidationConfiguration = createValidationConfiguration<FormModel>();

staticValidationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

staticValidationConfiguration.forScalar("description")
	.add(
		doesNotExceedLength(255)
	);

const focalPointValidator = staticValidationConfiguration.forObject("image")
	.whenType(isNotNil)
	.forObject("focalPoint");

focalPointValidator.forScalar("left")
	.add(
		isNotLessThan(0),
		doesNotExceed(1)
	);

type PermissionKind = "author" | "owner";

const permissionKindToAddMemberDialogConfigurations: Record<PermissionKind, { titleKey: LocalizationKey; bodyKey: LocalizationKey; }> = {
	["author"]: {
		titleKey: "_DoYouWantToAddTheseAuthorsAsMember",
		bodyKey: "_AddAuthorsAsMembersConfirmationDialogDescription"
	},
	["owner"]: {
		titleKey: "_DoYouWantToAddTheseOwnersAsMember",
		bodyKey: "_AddOwnersAsMembersConfirmationDialogDescription"
	}
};

function convertEditCommunityViewModelToFormModel(
	model: EditCommunityViewModel
): FormModel {
	const {
		authors,
		description,
		image,
		kind,
		name,
		members,
		owners
	} = model;

	return {
		name,
		description,
		image: isNil(image)
			? null
			: {
				file: { isSet: false },
				focalPoint: image.focalPoint
			},
		kind,
		members,
		owners,
		authors: {
			everyoneWhoHasAccess: authors.everyoneWhoHasAccess,
			specificSet: authors.everyoneWhoHasAccess
				? {
					userGroupIds: [],
					userIds: []
				}
				: authors.specificSet,
		}
	};
}

function convertFormModelToSaveCommunityModel(
	model: FormModel,
	existingMembers: readonly number[]
): SaveCommunityModel {
	const {
		authors,
		description,
		image,
		members,
		name,
		owners
	} = model;

	return {
		name,
		description,
		image,
		memberChanges: {
			added: without(members, existingMembers)
		},
		authors: authors.everyoneWhoHasAccess
			? { everyoneWhoHasAccess: true }
			: {
				everyoneWhoHasAccess: false,
				specificSet: authors.specificSet
			},
		owners: owners
	};
}

const defaultImageRestrictions: CommunityImageRestrictions = {
	maxFileSize: 0,
	supportedImageTypes: EMPTY_ARRAY
};

export type EditCommunityDialogInitialData = Omit<EditCommunityDialogProps, "onClose" | "afterSave">;

export function useEditCommunityDialog(): [
	element: ReactElement | null,
	open: (props: EditCommunityDialogInitialData) => Promise<TryResult<SaveCommunityResult>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => (
			<EditCommunityDialog
				{...props}
				onClose={onClose}
				afterSave={onSave}
			/>
		)
	);
}
